import { z } from 'zod';

/** architecture.md § 4. Transitions are enforced by a pure function, never by hand. */
export const ENVELOPE_STATUSES = [
  'draft', 'sent', 'delivered', 'partially_signed',
  'completed', 'declined', 'voided', 'expired',
] as const;
export type EnvelopeStatus = (typeof ENVELOPE_STATUSES)[number];

export const TERMINAL_STATUSES: readonly EnvelopeStatus[] = [
  'completed', 'declined', 'voided', 'expired',
];

export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;   // R-2.7
export const MAX_ENVELOPE_BYTES = 100 * 1024 * 1024;  // R-2.7
export const MAX_DOCUMENTS_PER_ENVELOPE = 10;         // R-2.7

export const envelopeCreateSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  /** Required. The eligibility gate (C-13) has no "unspecified" state by design. */
  documentCategory: z.string().min(1, 'Choose a document type'),
  internalNote: z.string().max(2000).optional(),
  messageToSigners: z.string().max(2000).optional(),
  /** Ids of warn-level categories the owner explicitly acknowledged. */
  acknowledgedWarnings: z.array(z.string()).max(10).optional(),
});
export type EnvelopeCreate = z.infer<typeof envelopeCreateSchema>;

export const finalizeUploadSchema = z.object({
  envelopeId: z.string().min(1).max(128),
  documentId: z.string().min(1).max(128),
});
export type FinalizeUpload = z.infer<typeof finalizeUploadSchema>;

/**
 * Removes a document from a draft. With `replacedBy` (an already-finalized
 * upload on the same envelope), the removed document's fields move to the
 * replacement and it takes the removed document's place in the order.
 */
export const removeDocumentSchema = z.object({
  envelopeId: z.string().min(1).max(128),
  documentId: z.string().min(1).max(128),
  replacedBy: z.string().min(1).max(128).optional(),
});
export type RemoveDocument = z.infer<typeof removeDocumentSchema>;

export interface DocumentRecord {
  id: string;
  name: string;
  order: number;
  storagePath: string;
  sizeBytes: number;
  pageCount: number;
  /** MediaBox dimensions in PDF points, per page. Cached so the client never parses the PDF. */
  pageSizes: { width: number; height: number }[];
  originalSha256: string;
  uploadedAt: string;
}

const UNSAFE_FILENAME = /[\x00-\x1f\x7f<>:"|?*\\/]/g;

/**
 * Strips path separators and control characters from an uploaded filename.
 *
 * Storage keys are derived from generated IDs rather than this value, so the
 * name is never used as a path. It IS rendered in the UI and in the Certificate
 * of Completion, though, so it stays untrusted display input and is cleaned here
 * at the boundary rather than at each render site.
 */
export function sanitizeFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? 'document.pdf';
  const cleaned = base.replace(UNSAFE_FILENAME, '').trim().slice(0, 255);
  return cleaned.length > 0 ? cleaned : 'document.pdf';
}

/** Human-readable byte size for the UI. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
